/**
 * Dashboard boot: render the shell, wire the store to the DOM, and kick off
 * the relay subscription. Teardown on page unload closes the subscription
 * (Gate 2.6b). Connection state is tracked so the header live-dot reflects
 * real relay connectivity, and a manual "Reconnect" button re-establishes
 * the subscription when the socket drops (T6).
 */
import './style.css';
import { CiStore, sortRuns } from './store';
import { subscribeCiEvents } from './relay';
import { parseProgress, parseJobResult, parseWorkflowResult } from './lib/parse';
import { mountHeader, renderRunList, renderJobListPane } from './render';
import { el } from './components/runs-list';
import { COORDINATOR_PUBKEY, RELAYS, RUN_RETENTION_MS } from './config';

const app = document.querySelector<HTMLDivElement>('#app');
if (!app) {
  throw new Error('Missing #app root element');
}

const store = new CiStore({ retentionMs: RUN_RETENTION_MS });

// Dashboard header: relay names + coordinator pubkey + connection live-dot.
// The live-dot reflects the real relay connection state (T6). `mountHeader`
// owns the live header node and swaps it on every connection change, so later
// transitions keep repainting (see mountHeader's doc comment for why a
// boot-time const node freezes the indicator and the Reconnect button).
const setHeaderConnected = mountHeader(app, {
  relays: RELAYS,
  coordinatorPubkey: COORDINATOR_PUBKEY,
  connected: false,
  onReconnect: () => subscription.reconnect(),
});

const runListEl = document.createElement('div');
runListEl.id = 'run-list';
app.append(runListEl);

const jobPaneEl = document.createElement('div');
jobPaneEl.id = 'job-pane';
app.append(jobPaneEl);

/** Currently selected run id (T5 renders its job list). */
let selectedRunId: string | undefined;

/** Re-render the run list and the selected run's job list from the store. */
function render(): void {
  const runs = sortRuns(store.snapshot().values());
  runListEl.replaceChildren(renderRunList(runs, Date.now(), selectedRunId, (id) => {
    selectedRunId = id;
    render();
  }));
  const selected = selectedRunId ? store.snapshot().get(selectedRunId) : undefined;
  jobPaneEl.replaceChildren(selected ? renderJobListPane(selected) : el('p', 'job-pane-empty', 'Select a run to see its jobs.'));
}

store.subscribe(render);
render();

// Prune stale runs periodically.
const pruneTimer = window.setInterval(() => store.prune(), 60_000);

// Subscribe to the coordinator's CI events and feed them into the store.
const subscription = subscribeCiEvents({
  39842: (ev) => store.ingest(parseProgress(ev)),
  9841: (ev) => store.ingest(parseJobResult(ev)),
  9842: (ev) => store.ingest(parseWorkflowResult(ev)),
});

// Reflect real relay connectivity in the header live-dot + connection-lost
// indicator. Re-render the header (not the whole page) on state changes.
subscription.onConnectionChange((isConnected) => {
  setHeaderConnected(isConnected);
});

// Teardown on page unload: close the relay subscription and stop timers.
window.addEventListener('beforeunload', () => {
  subscription.close();
  window.clearInterval(pruneTimer);
});

// Dev-only E2E test hook (T7): expose the store and the pure parse functions
// so Playwright can seed deterministic fixture events and drive a synthetic
// live transition. Seeding goes through the same store.ingest -> notify ->
// render path as a real relay event, so the E2E tour exercises production
// code. Guarded to the dev server so it never ships in a production build.
if (import.meta.env.DEV) {
  const w = window as unknown as {
    __store?: { ingest: (e: unknown) => void };
    __parse?: {
      progress: (r: unknown) => unknown;
      jobResult: (r: unknown) => unknown;
      workflowResult: (r: unknown) => unknown;
    };
  };
  w.__store = { ingest: (e) => store.ingest(e as Parameters<typeof store.ingest>[0]) };
  w.__parse = {
    progress: (r) => parseProgress(r as Parameters<typeof parseProgress>[0]),
    jobResult: (r) => parseJobResult(r as Parameters<typeof parseJobResult>[0]),
    workflowResult: (r) => parseWorkflowResult(r as Parameters<typeof parseWorkflowResult>[0]),
  };
}
